import type { ShareLink, SharedTripApi, TripDetails } from './types'
import { SharedTripApiError } from './types'
import { tripDetailsCsvBlob } from './tripCsv'

const exampleTrip: TripDetails = {
  id: '239143',
  ownerName: 'Demo traveller',
  description: 'Evening walk in Stockholm',
  status: 'ended',
  visibility: 'pals',
  startedAt: '2026-07-24T18:00:00Z',
  endedAt: '2026-07-24T18:42:00Z',
  durationSeconds: 2520,
  distanceKilometers: 3.275,
  averageSpeedKilometersPerHour: 4.7,
  platform: 'iOS',
  views: 42,
  positions: [
    { id: '900001', latitude: 59.3293, longitude: 18.0686, altitudeMeters: 16, speedMetersPerSecond: 1.3, distanceKilometers: 0, recordedAt: '2026-07-24T18:00:00Z' },
    { id: '900002', latitude: 59.3316, longitude: 18.0751, altitudeMeters: 20, speedMetersPerSecond: 1.2, distanceKilometers: 1.64, comment: 'A beautiful view across the water.', recordedAt: '2026-07-24T18:21:00Z' },
    { id: '900003', latitude: 59.334, longitude: 18.0817, altitudeMeters: 18, speedMetersPerSecond: 1.1, distanceKilometers: 3.275, recordedAt: '2026-07-24T18:42:00Z' },
  ],
}

const liveExampleTrip: TripDetails = {
  ...exampleTrip,
  id: '239368',
  description: 'Live freeway test',
  status: 'running',
  endedAt: undefined,
}

const nextLivePosition = {
  id: '900004',
  latitude: 59.3364,
  longitude: 18.0882,
  altitudeMeters: 19,
  speedMetersPerSecond: 1.2,
  distanceKilometers: 4.1,
  recordedAt: '2026-07-24T18:52:00Z',
}

export class MockMapapalApi implements SharedTripApi {
  async getSharedTrip(link: ShareLink, signal?: AbortSignal): Promise<TripDetails> {
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(resolve, 120)
      signal?.addEventListener('abort', () => {
        window.clearTimeout(timeout)
        reject(new DOMException('Aborted', 'AbortError'))
      }, { once: true })
    })

    if (link.userNo !== '29728') {
      throw new SharedTripApiError(
        'not-found-or-denied',
        'Trip not found or access was denied.',
      )
    }

    if (link.tripId === exampleTrip.id) return structuredClone(exampleTrip)
    if (link.tripId === liveExampleTrip.id) return structuredClone(liveExampleTrip)
    throw new SharedTripApiError(
      'not-found-or-denied',
      'Trip not found or access was denied.',
    )
  }

  async refreshSharedTrip(
    link: ShareLink,
    lastPositionId: string,
    signal?: AbortSignal,
  ): Promise<TripDetails> {
    const trip = await this.getSharedTrip(link, signal)
    if (trip.id === liveExampleTrip.id && lastPositionId === trip.positions.at(-1)?.id) {
      return { ...trip, positions: [structuredClone(nextLivePosition)] }
    }
    const lastIndex = trip.positions.findIndex((position) => position.id === lastPositionId)
    return { ...trip, positions: trip.positions.slice(lastIndex + 1) }
  }

  async getSharedTripCsv(link: ShareLink, signal?: AbortSignal): Promise<Blob> {
    return tripDetailsCsvBlob(await this.getSharedTrip(link, signal))
  }
}
